import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import type {
  ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexPlotOptions,
  ApexXAxis, ApexYAxis, ApexLegend, ApexTooltip, ApexGrid
} from 'ng-apexcharts';
import { MfgappsApiService } from '../../../../../services/mfgapps-api.service';
import type {
  ConnectivitySummary, ConnectivitySummaryLineBar, ConnectivitySummaryNamc,
  ConnectivitySummaryShop, ConnectivitySummaryStatus
} from '../../../shared/models/connectivity-summary.models';

export interface ApexChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  colors: string[];
  dataLabels: ApexDataLabels;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: ApexGrid;
}

interface ConnectivityNamcRow extends ConnectivitySummaryNamc {
  equipmentConnected: number | null;
  equipmentPlanned: number | null;
  equipmentConnectivityPct: number | null;
  pdmSensorsConfigured: number | null;
  pdmSensorsPlanned: number | null;
  pdmSensorsConnectivityPct: number | null;
}

@Component({
  selector: 'app-deployment-connectivity',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: './deployment-connectivity.component.html',
  styleUrl: './deployment-connectivity.component.scss'
})
export class DeploymentConnectivityComponent implements OnInit {
  summary: ConnectivitySummary | null = null;
  summaryLoading = false;
  summaryError = '';
  connectivityNamcRows: ConnectivityNamcRow[] = [];
  connExpandedNamcs: Record<string, boolean> = {};
  deploymentVsConnectedChart!: ApexChartOptions;
  connectivityByNamcChart!: ApexChartOptions;
  connectivityBySourceChart!: ApexChartOptions;

  constructor(private readonly apiService: MfgappsApiService) {}

  ngOnInit(): void {
    void this.loadConnectivitySummary();
  }

  async loadConnectivitySummary(): Promise<void> {
    if (this.summaryLoading) {
      return;
    }
    this.summaryLoading = true;
    this.summaryError = '';
    this.summary = null;
    this.connectivityNamcRows = [];
    this.connExpandedNamcs = {};
    try {
      const response = await this.apiService.getConnectivitySummary();
      const summary = response?.result;
      if (!summary || !Array.isArray(summary.cards) || !Array.isArray(summary.tree_view)
        || !summary.line_bars?.LineBar1?.datapoints
        || !summary.line_bars?.LineBar2?.datapoints
        || !summary.line_bars?.LineBar3?.datapoints) {
        throw new Error('Invalid connectivity summary response');
      }

      this.connectivityNamcRows = summary.tree_view.map(namc => this.mapNamc(namc));
      // Color each NAMC bar by its own API-reported status so the chart matches
      // the status shown for that NAMC in the table below, instead of a flat
      // percentage cutoff that doesn't fit this metric's realistic value range.
      const namcStatusByName: Record<string, ConnectivitySummaryStatus> = {};
      summary.tree_view.forEach(namc => { namcStatusByName[namc.name] = namc.status; });

      this.deploymentVsConnectedChart = this.buildBarChart(summary.line_bars.LineBar1, false);
      this.connectivityByNamcChart = this.buildBarChart(summary.line_bars.LineBar2, true, namcStatusByName);
      this.connectivityBySourceChart = this.buildBarChart(summary.line_bars.LineBar3, true);
      this.summary = summary;
    } catch (error) {
      console.info('Unable to load connectivity summary', error);
      this.connectivityNamcRows = [];
      this.summaryError = 'Unable to load connectivity summary. Please try again.';
    } finally {
      this.summaryLoading = false;
    }
  }

  get connectivityRate(): number | null {
    const card = this.summary?.cards.find(item => item.title === 'Connectivity Rate');
    if (card) {
      return card.count;
    }
    const points = this.summary?.line_bars.LineBar1.datapoints;
    return this.percentage(points?.['Connected'] ?? null, points?.['Provided'] ?? null);
  }

  private mapNamc(namc: ConnectivitySummaryNamc): ConnectivityNamcRow {
    const children = namc.children ?? [];
    const equipmentConnected = this.sumShopField(children, 'equipmentConnected');
    const equipmentPlanned = this.sumShopField(children, 'equipmentPlanned');
    const pdmSensorsConfigured = this.sumShopField(children, 'pdmSensorsConfigured');
    const pdmSensorsPlanned = this.sumShopField(children, 'pdmSensorsPlanned');
    // Category totals are not supplied at NAMC level. Keep the API's overall
    // percent_connected/status; only derive equipment and sensor breakdowns.
    return {
      ...namc,
      children,
      equipmentConnected,
      equipmentPlanned,
      equipmentConnectivityPct: this.percentage(equipmentConnected, equipmentPlanned),
      pdmSensorsConfigured,
      pdmSensorsPlanned,
      pdmSensorsConnectivityPct: this.percentage(pdmSensorsConfigured, pdmSensorsPlanned)
    };
  }

  private sumShopField(
    shops: ConnectivitySummaryShop[],
    field: 'equipmentConnected' | 'equipmentPlanned' | 'pdmSensorsConfigured' | 'pdmSensorsPlanned'
  ): number | null {
    // Missing data must not be presented as zero or as a complete partial total.
    if (shops.length === 0 || shops.some(shop => shop[field] == null)) {
      return null;
    }
    return shops.reduce((sum, shop) => sum + (shop[field] ?? 0), 0);
  }

  private percentage(connected: number | null, planned: number | null): number | null {
    return connected !== null && planned !== null && planned > 0
      ? Math.round(connected / planned * 1000) / 10
      : null;
  }

  percentageStatus(pct: number | null): ConnectivitySummaryStatus {
    if (pct === null) { return 'N/A'; }
    if (pct >= 70) { return 'Good'; }
    if (pct >= 40) { return 'Warning'; }
    return 'Critical';
  }

  toggleConnNamc(namc: string): void {
    this.connExpandedNamcs[namc] = !this.connExpandedNamcs[namc];
  }

  isConnNamcExpanded(namc: string): boolean {
    return !!this.connExpandedNamcs[namc];
  }

  connStatusClass(status: ConnectivitySummaryStatus): string {
    return {
      Good: 'status--good', Warning: 'status--warn',
      Critical: 'status--critical', 'N/A': 'status--na'
    }[status];
  }

  barClass(status: ConnectivitySummaryStatus): string {
    return {
      Good: 'bar-fill--connected', Warning: 'bar-fill--warn',
      Critical: 'bar-fill--critical', 'N/A': 'bar-fill--none'
    }[status];
  }

  trackByName(_: number, row: { name: string }): string { return row.name; }

  private statusColor(status: ConnectivitySummaryStatus): string {
    return {
      Good: '#31a36f', Warning: '#f5a623',
      Critical: '#e55c5c', 'N/A': '#c9d3da'
    }[status];
  }

  private chartColor(
    label: string, value: number, percent: boolean, statusByLabel?: Record<string, ConnectivitySummaryStatus>
  ): string {
    if (!percent) {
      const colors: Record<string, string> = { Provided: '#3d7cbf', Connected: '#31a36f', Missing: '#e55c5c' };
      return colors[label] ?? '#3d7cbf';
    }
    // Prefer the API's own status for this label (e.g. a NAMC's reported status)
    // so chart colors stay consistent with the status shown elsewhere for the
    // same entity, falling back to percentage-based thresholds otherwise.
    const status = statusByLabel?.[label] ?? this.percentageStatus(value);
    return this.statusColor(status);
  }

  private buildBarChart(
    bar: ConnectivitySummaryLineBar, percent: boolean, statusByLabel?: Record<string, ConnectivitySummaryStatus>
  ): ApexChartOptions {
    const entries = Object.entries(bar.datapoints);
    const format = (value: string | number): string => percent
      ? `${Number(value).toFixed(1)}%` : Number(value).toLocaleString();
    return {
      series: [{ name: percent ? '% Connected' : 'Devices', data: entries.map(([, value]) => value) }],
      chart: { type: 'bar', height: 260, toolbar: { show: false }, fontFamily: 'inherit' },
      plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: '55%' } },
      colors: entries.map(([label, value]) => this.chartColor(label, value, percent, statusByLabel)),
      dataLabels: { enabled: true, formatter: format, style: { colors: ['#1f2937'] } },
      xaxis: {
        categories: entries.map(([label]) => label),
        ...(percent ? { max: 100 } : {}),
        labels: { formatter: format }
      },
      yaxis: {},
      legend: { show: false },
      tooltip: { y: { formatter: format } },
      grid: { borderColor: '#eef0f2' }
    };
  }
}